// Códigos de retención (financiero.retencion_codigos). Financiero → Configuración.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, Switch, fin, fieldCls } from './shared';

interface Codigo { id: string; codigo_impuesto: string; codigo_retencion: string; descripcion: string; porcentaje: number; es_activo: boolean }
const vacio = { codigo_impuesto: '', codigo_retencion: '', descripcion: '', porcentaje: 0, es_activo: true };

export function RetencionCodigos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Codigo[]>({ queryKey: ['adm-retencion'], queryFn: () => fin.list<Codigo>('retencion_codigos', '?select=*&order=codigo_impuesto,codigo_retencion') });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (c?: Codigo) => {
    if (c) setForm({ codigo_impuesto: c.codigo_impuesto, codigo_retencion: c.codigo_retencion, descripcion: c.descripcion, porcentaje: c.porcentaje, es_activo: c.es_activo });
    else setForm(vacio);
    setEditId(c?.id ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form };
      return editId ? fin.update('retencion_codigos', editId, body) : fin.create('retencion_codigos', body);
    },
    onSuccess: () => { toast.success('Código guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-retencion'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => fin.remove('retencion_codigos', id),
    onSuccess: () => { toast.success('Código eliminado'); void qc.invalidateQueries({ queryKey: ['adm-retencion'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Códigos de Retención</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo código</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Códigos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Cód. impuesto</th><th className="py-2 pr-3">Cód. retención</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">%</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin códigos</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.codigo_impuesto}</td>
                  <td className="py-2 pr-3 font-mono">{c.codigo_retencion}</td>
                  <td className="py-2 pr-3">{c.descripcion}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{Number(c.porcentaje).toFixed(2)}%</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.descripcion}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar código' : 'Nuevo código'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código impuesto *"><input className={fieldCls} value={form.codigo_impuesto} onChange={(e) => setForm({ ...form, codigo_impuesto: e.target.value })} /></Campo>
              <Campo label="Código retención *"><input className={fieldCls} value={form.codigo_retencion} onChange={(e) => setForm({ ...form, codigo_retencion: e.target.value })} /></Campo>
            </div>
            <Campo label="Descripción *"><input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Campo label="Porcentaje (%)"><input type="number" step="0.01" className={fieldCls} value={form.porcentaje} onChange={(e) => setForm({ ...form, porcentaje: Number(e.target.value) })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.codigo_impuesto.trim() || !form.codigo_retencion.trim() || !form.descripcion.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
